/**
 * Demo Data Utilities
 *
 * Provides realistic, attractive demo data for pre-active customers.
 * This allows customers to explore the TACH platform before their account is fully activated.
 *
 * Design Philosophy:
 * - Realistic numbers that represent a successful trucking business
 * - Positive cash flow and growth trends
 * - Professional, aspirational data that showcases platform value
 * - Consistent data across all widgets
 */

// Note: These types come from auto-generated apiTypes.ts
// They may be regenerated, but the demo data structure should remain compatible
import type {
  BalancesResponse,
  RevenueExpensesData,
  BudgetItemComputed,
} from './demoDataTypes';

// ============================================================================
// Demo Balances
// ============================================================================

export const getDemoBalances = (): BalancesResponse => {
  return {
    grossProfit: 13518.32,
    savingPacePercentageDifference: 108, // 8.5% ahead of pace
    budgetedBalance: 30635.0,
    pendingTransfer: 2450.0,
    availableTakeHomePay: 0, // default-toggle state; conditional row hidden
    cashAdvanceLimit: 25000,
    cashAdvanceAvailable: 25000,
    isCashAdvanceActive: true,
    mtdRevenue: 24578.2, // Month-to-date revenue
    momRevenuePercentageDifference: 12.3, // 12.3% growth month-over-month
  };
};

// ============================================================================
// Demo Revenue & Expenses Chart Data
// ============================================================================

export const getDemoRevenueExpensesData = (): RevenueExpensesData[] => {
  // Get the last 6 months as Date objects
  const currentDate = new Date();
  const months: Date[] = [];

  for (let i = 5; i >= 0; i--) {
    const date = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() - i,
      1
    );
    months.push(date);
  }

  // Generate realistic upward trending data
  return months.map((monthDate, index) => {
    const baseRevenue = 22000;
    const growthFactor = 1 + index * 0.08; // 8% monthly growth
    const variance = Math.random() * 2000 - 1000; // Add some natural variance

    const revenue = baseRevenue * growthFactor + variance;
    const expenses = revenue * (0.55 - index * 0.02); // Improving margins

    return {
      month: monthDate.getMonth().toString(), // Numeric month (0-11) as string, as expected by the chart component
      revenue: Math.round(revenue * 100) / 100,
      expenses: Math.round(expenses * 100) / 100,
    } as RevenueExpensesData;
  });
};

// ============================================================================
// Demo Budget/Savings Items
// ============================================================================

export const getDemoBudgetItems = (): BudgetItemComputed[] => {
  // For a carrier making $25k/month with $13,750 budgeted for expenses (55%)
  // All amounts are in actual dollars
  return [
    {
      category: 'fuel',
      category_display_name: 'Fuel',
      emoji_unicode: 'U+26FD',
      budget_bucket_limit: 9000.0, // $9,000/month budget (36% of revenue)
      amount: -1500.0, // NEGATIVE: -$1,500 (overspent due to fuel price spike)
      time_window_in_months: 1,
      rollover: -1500.0, // Negative balance carries over
      spent: 10500.0, // Spent $10,500 when budget was only $9,000
      saved: 7000.0,
      isCustom: false,
      monthly_goal: 9000.0,
    },
    {
      category: 'repairs',
      category_display_name: 'Repairs & Parts',
      emoji_unicode: 'U+1F6E0',
      budget_bucket_limit: 18000.0, // $18,000/year budget ($1,500/month)
      amount: 22000.0, // OVERFILLED: $22,000 (saved $4,000 more than annual goal)
      time_window_in_months: 12,
      rollover: 22000.0, // Extra savings roll over
      spent: 1250.0,
      saved: 4000.0, // Saved $4,000 beyond the goal
      isCustom: false,
      monthly_goal: 1500.0, // $1,500/month
    },
    {
      category: 'tires',
      category_display_name: 'Tires',
      emoji_unicode: 'U+1F6DE',
      budget_bucket_limit: 6000.0, // $6,000/year budget ($500/month)
      amount: 3000.0, // $3,000 current balance (50% funded)
      time_window_in_months: 12,
      rollover: 3000.0,
      spent: 0,
      saved: 0,
      isCustom: false,
      monthly_goal: 500.0, // $500/month
    },
    {
      category: 'preventative_maintenance',
      category_display_name: 'PMs',
      emoji_unicode: 'U+1F527',
      budget_bucket_limit: 1500.0, // $1,500/3 months budget ($500/month)
      amount: 825.0, // $825 current balance (55% funded)
      time_window_in_months: 3,
      rollover: 825.0,
      spent: 0,
      saved: 0,
      isCustom: false,
      monthly_goal: 500.0, // $500/month
    },
    {
      category: 'licensing',
      category_display_name: 'Licensing & Fees',
      emoji_unicode: 'U+1F4C4',
      budget_bucket_limit: 2400.0, // $2,400/year budget ($200/month)
      amount: 1800.0, // $1,800 current balance (75% funded)
      time_window_in_months: 12,
      rollover: 1800.0,
      spent: 0,
      saved: 0,
      isCustom: false,
      monthly_goal: 200.0, // $200/month
    },
    {
      category: 'tolls',
      category_display_name: 'Tolls',
      emoji_unicode: 'U+1F6E3',
      budget_bucket_limit: 4200.0, // $4,200/year budget ($350/month)
      amount: 2940.0, // $2,940 current balance (70% funded)
      time_window_in_months: 12,
      rollover: 2940.0,
      spent: 0,
      saved: 0,
      isCustom: false,
      monthly_goal: 350.0, // $350/month
    },
    {
      category: 'insurance',
      category_display_name: 'Insurance',
      emoji_unicode: 'U+1F6E1',
      budget_bucket_limit: 1200.0, // $1,200/month budget (4.8% of revenue)
      amount: 1080.0, // $1,080 current balance (90% funded)
      time_window_in_months: 1,
      rollover: 1080.0,
      spent: 0,
      saved: 0,
      isCustom: false,
      monthly_goal: 1200.0,
    },
    {
      category: 'truck_payments',
      category_display_name: 'Truck Payments',
      emoji_unicode: 'U+1F69A',
      budget_bucket_limit: 0, // Paid separately outside TACH
      amount: 0,
      time_window_in_months: 1,
      rollover: 0,
      spent: 0,
      saved: 0,
      isCustom: false,
      monthly_goal: 0,
    },
    {
      category: 'custom_new_truck_fund',
      category_display_name: 'New Truck Fund',
      emoji_unicode: 'U+1F6FB',
      budget_bucket_limit: 500.0, // $500/month budget
      amount: 490.0, // $490 current balance (98% funded)
      time_window_in_months: 1,
      rollover: 490.0,
      spent: 0,
      saved: 0,
      isCustom: true,
      customer_selected_amount: 500.0,
      monthly_goal: 500.0,
    },
  ];
};

// ============================================================================
// Demo Account Info
// ============================================================================

export const getDemoAccountInfo = () => {
  return {
    account_id: 'demo-account-id',
    customer_id: 'demo-customer',
    account_number: '1234567890',
    routing_number: '053101561',
    account_type: 'checking',
    created_at: new Date('2024-01-15'),
    updated_at: new Date(),
  };
};

// ============================================================================
// Demo Monthly Statements
// ============================================================================

export const getDemoMonthlyStatements = () => {
  const currentDate = new Date();
  const statements = [];

  // Generate last 6 months of statements
  for (let i = 0; i < 6; i++) {
    const statementDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() - i,
      1
    );

    statements.push({
      statement_id: `demo-statement-${i}`,
      customer_id: 'demo-customer',
      month: statementDate.getMonth() + 1, // 1-based month
      year: statementDate.getFullYear(),
      statement_url: null,
      created_at: statementDate,
      updated_at: statementDate,
    });
  }

  return statements;
};

// ============================================================================
// Demo Loans (Cash Advances)
// ============================================================================

export const getDemoLoans = (): any[] => {
  const today = new Date();

  return [
    // Active Cash Advances
    {
      loan_id: 'demo-loan-1',
      customer_id: 'demo-customer',
      loan_request_id: 'demo-loan-request-1',
      total_cost_of_loan: 197,
      current_balance: 175,
      paid: 22,
      end_date: new Date(today.getFullYear(), today.getMonth() + 1, 15),
      originated_at: new Date(today.getFullYear(), today.getMonth() - 2, 1),
      status: 'active',
      type: 'cash_advance',
      loanRequest: {
        reason: 'Reparing a damaged clutch',
        loan_request_id: 'demo-loan-request-1',
      },
    },
    {
      loan_id: 'demo-loan-2',
      customer_id: 'demo-customer',
      loan_request_id: 'demo-loan-request-2',
      total_cost_of_loan: 8430,
      current_balance: 2400,
      paid: 5600,
      end_date: new Date(today.getFullYear(), today.getMonth() + 2, 20),
      originated_at: new Date(today.getFullYear(), today.getMonth() - 3, 15),
      status: 'active',
      type: 'cash_advance',
      loanRequest: {
        reason: 'Maintenance & Repairs',
        loan_request_id: 'demo-loan-request-2',
      },
    },
    // Active Fuel Advances
    {
      loan_id: 'demo-loan-3',
      customer_id: 'demo-customer',
      loan_request_id: 'demo-loan-request-3',
      total_cost_of_loan: 4500,
      current_balance: 1800,
      paid: 2700,
      end_date: new Date(today.getFullYear(), today.getMonth() + 1, 25),
      originated_at: new Date(today.getFullYear(), today.getMonth() - 1, 10),
      status: 'active',
      type: 'fuel_advance',
      loanRequest: {
        reason: 'Diesel Fuel',
        loan_request_id: 'demo-loan-request-3',
      },
    },
    {
      loan_id: 'demo-loan-4',
      customer_id: 'demo-customer',
      loan_request_id: 'demo-loan-request-4',
      total_cost_of_loan: 3200,
      current_balance: 950,
      paid: 2250,
      end_date: new Date(today.getFullYear(), today.getMonth() + 3, 5),
      originated_at: new Date(today.getFullYear(), today.getMonth() - 2, 20),
      status: 'active',
      type: 'fuel_advance',
      loanRequest: {
        reason: 'Fuel for West Coast Route',
        loan_request_id: 'demo-loan-request-4',
      },
    },
  ];
};

// ============================================================================
// Demo Recent Deposits (Revenue Transactions)
// ============================================================================

export const getDemoRecentDeposits = () => {
  const today = new Date();
  const transactions: any[] = [];

  // Generate realistic deposit transactions
  const deposits = [
    {
      amount: 4850.0,
      category: 'income',
      name: 'Load #2847 - Chicago to Dallas',
      daysAgo: 1,
    },
    {
      amount: 5200.5,
      category: 'income',
      truck_id: 'demo-truck-1',
      name: 'Load #2846 - Memphis to Atlanta',
      daysAgo: 3,
    },
    {
      amount: 3975.25,
      category: 'income',
      truck_id: 'demo-truck-1',
      name: 'Load #2845 - Houston to Phoenix',
      daysAgo: 5,
    },
    {
      amount: 6100.0,
      category: 'income',
      truck_id: 'demo-truck-2',
      name: 'Load #2844 - LA to Seattle',
      daysAgo: 7,
    },
    {
      amount: 4450.75,
      category: 'income',
      truck_id: 'demo-truck-2',
      name: 'Load #2843 - Denver to Kansas City',
      daysAgo: 9,
    },
    {
      amount: 5500.0,
      category: 'income',
      name: 'Load #2842 - Portland to Sacramento',
      daysAgo: 11,
      truck_id: 'demo-truck-1',
    },
    {
      amount: 4200.5,
      category: 'income',
      truck_id: 'demo-truck-1',
      name: 'Load #2841 - Miami to Orlando',
      daysAgo: 13,
    },
    {
      amount: 5850.25,
      category: 'income',
      truck_id: 'demo-truck-2',
      name: 'Load #2840 - Boston to NYC',
      daysAgo: 15,
    },
  ];

  deposits.forEach((deposit, index) => {
    const date = new Date(today);
    date.setDate(date.getDate() - deposit.daysAgo);

    transactions.push({
      transaction_id: `demo-deposit-${index}`,
      customer_id: 'demo-customer',
      amount: deposit.amount,
      date: date.toISOString(),
      description: deposit.name,
      category: 'income',
      truck_id: deposit.truck_id,
      merchant_name: 'Factoring Company',
      pending: false,
      iso_currency_code: 'USD',
      authorized_date: date.toISOString(),
      created_at: date,
      updated_at: date,
    });
  });

  return transactions;
};

// ============================================================================
// Demo Recent Expenses
// ============================================================================

export const getDemoRecentExpenses = () => {
  const today = new Date();
  const transactions: any[] = [];

  const expenses = [
    {
      amount: -485.5,
      name: 'Pilot Flying J - Fuel',
      category: 'fuel',
      daysAgo: 1,
    },
    {
      amount: -2452.25,
      name: 'TA Petro - Parking',
      category: 'repairs',
      daysAgo: 2,
    },
    {
      amount: -420.0,
      name: "Love's Travel Stop - Fuel",
      truck_id: 'demo-truck-1',
      category: 'fuel',
      daysAgo: 2,
    },
    {
      amount: -945.75,
      name: 'Tire Shop - Maintenance',
      truck_id: 'demo-truck-1',
      category: 'preventative_maintenance',
      daysAgo: 4,
    },
    {
      amount: -1189.0,
      name: 'Truck Wash Express',
      truck_id: 'demo-truck-2',
      category: 'tires',
      daysAgo: 5,
    },
    {
      amount: -465.25,
      name: 'Speedway - Fuel',
      truck_id: 'demo-truck-2',
      category: 'fuel',
      daysAgo: 6,
    },
    {
      amount: -325.5,
      name: 'Road Ranger - Supplies',
      truck_id: 'demo-truck-1',
      category: 'fuel',
      daysAgo: 7,
    },
    {
      amount: -895.0,
      name: 'Shell - Fuel',
      truck_id: 'demo-truck-1',
      category: 'insurance',
      daysAgo: 8,
    },
  ];

  expenses.forEach((expense, index) => {
    const date = new Date(today);
    date.setDate(date.getDate() - expense.daysAgo);

    transactions.push({
      transaction_id: `demo-expense-${index}`,
      customer_id: 'demo-customer',
      amount: expense.amount,
      date: date.toISOString(),
      description: expense.name,
      category: expense.category,
      merchant_name: expense.name.split(' - ')[0],
      pending: index === 0, // First transaction is pending
      iso_currency_code: 'USD',
      authorized_date: date.toISOString(),
      truck_id: expense.truck_id,
      created_at: date,
      updated_at: date,
      // Mark a couple expenses as reversed for testing
      reversal_status: index === 2 || index === 5 ? 'reversed' : null,
    });
  });

  return transactions;
};

// ============================================================================
// Demo Trucks
// ============================================================================

export const getDemoTrucks = () => {
  return [
    {
      truck_id: 'demo-truck-1',
      customer_id: 'demo-customer',
      make: 'Freightliner',
      model: 'Cascadia',
      year: 2020,
      vin: 'DEMO123456789ABCD',
      license_plate: 'TX-1234',
      truck_display_name: 'Unit 1',
      license_plate_state: 'TX',
      nickname: 'Big Blue',
      created_at: new Date('2024-01-01'),
      updated_at: new Date(),
    },
    {
      truck_id: 'demo-truck-2',
      customer_id: 'demo-customer',
      make: 'Peterbilt',
      model: '579',
      year: 2019,
      vin: 'DEMO987654321WXYZ',
      license_plate: 'TX-5678',
      license_plate_state: 'TX',
      nickname: 'Thunder',
      truck_display_name: 'Unit 2',
      created_at: new Date('2024-01-01'),
      updated_at: new Date(),
    },
  ];
};

// ============================================================================
// Demo Debit Cards
// ============================================================================

export const getDemoCards = () => {
  return [
    {
      debit_card_id: '1',
      last_four_digits: '1200',
      customer_id: 'demo-customer',
      truck_id: '1',
      account_id: '1',
      address_id: '1',
      frozen: false,
      is_cancelled: false,
      created_at: '2025-08-08T12:19:56.232Z',
      updated_at: '2025-09-19T15:33:40.123Z',
      cancelled_at: null,
      bank_debit_card_external_id: 'blue_penguin#1',
      daily_spend_limit: 600,
      thirty_day_spend_limit: 12500,
      physical_card_activated_timestamp: '2025-09-04T18:11:33.735Z',
      banned_spend_categories: [
        'Gambling',
        'Adult Entertainment',
        'Gaming & Casinos',
        'Jewelry & Watches',
        'Tattoo & Piercing',
        'Amusement Parks',
        'Pawn Shops',
      ],
      truck: {
        truck_id: '1',
        customer_id: 'demo-customer',
        created_at: '2025-08-04T16:48:49.396Z',
        vehicle_id_number: '2INGSEINOEISNF',
        truck_display_name: 'Unit 1',
        truck_make: 'KENWORTH',
        truck_model: 'T3 Series',
        truck_year: 2016,
        ownership_status: 'owned',
        engine_condition: 'unmodified',
        truck_class: 'Heavy Duty',
        truck_class_number: 7,
        starting_mileage: 4000,
        current_mileage: 300,
        typical_annual_mileage: 300,
        tire_brand_quality: 'budget',
        truck_payment_amount: '1200.00',
        insurance_payment_amount: '12.00',
        pay_for_repairs: true,
        external_eld_vehicle_id: null,
        eld_id: null,
        eld_type: null,
        eld_vehicle_linked_at: null,
        deleted_at: null,
        is_deleted: false,
        show_asset_image: false,
      },
      address: {
        address_id: '1',
        customer_id: 'demo-customer',
        address_st_number: '123',
        address_st_name: 'Main St',
        address_unit: null,
        address_city: 'Atlanta',
        address_state: 'GA',
        address_zip: '30328',
        is_primary: true,
        is_deleted: false,
        created_at: '2025-09-17T00:49:40.666Z',
        updated_at: '2025-09-17T00:49:40.666Z',
        deleted_at: null,
      },
    },
    {
      debit_card_id: '2',
      last_four_digits: '2400',
      customer_id: 'demo-customer',
      truck_id: '2',
      account_id: '2',
      address_id: '2',
      frozen: false,
      is_cancelled: false,
      created_at: '2025-09-03T23:18:13.215Z',
      updated_at: '2025-09-03T23:18:14.814Z',
      cancelled_at: null,
      bank_debit_card_external_id: 'blue_penguin#2',
      daily_spend_limit: 1000,
      thirty_day_spend_limit: 10000,
      physical_card_activated_timestamp: '2025-09-10T14:05:53.510Z',
      banned_spend_categories: ['Gambling'],
      truck: {
        truck_id: '2',
        customer_id: 'demo-customer',
        created_at: '2025-09-03T23:17:15.876Z',
        vehicle_id_number: '3JKENGLSEGSE',
        truck_display_name: 'Unit 4',
        truck_make: 'international',
        truck_model: 'lt62f',
        truck_year: 2023,
        ownership_status: 'owned',
        engine_condition: 'unmodified',
        truck_class: 'Heavy Duty',
        truck_class_number: 8,
        starting_mileage: 25,
        current_mileage: 350,
        typical_annual_mileage: 350,
        tire_brand_quality: 'premium',
        truck_payment_amount: '100.00',
        insurance_payment_amount: '400.00',
        pay_for_repairs: true,
        external_eld_vehicle_id: null,
        eld_id: null,
        eld_type: null,
        eld_vehicle_linked_at: null,
        deleted_at: null,
        is_deleted: false,
        show_asset_image: false,
      },
      address: {
        address_id: '2',
        customer_id: 'demo-customer',
        address_st_number: '123',
        address_st_name: 'Main St',
        address_unit: null,
        address_city: 'Atlanta',
        address_state: 'GA',
        address_zip: '30328',
        is_primary: true,
        is_deleted: false,
        created_at: '2025-09-17T00:49:40.666Z',
        updated_at: '2025-09-17T00:49:40.666Z',
        deleted_at: null,
      },
    },
    {
      debit_card_id: '3',
      last_four_digits: '1111',
      customer_id: 'demo-customer',
      truck_id: '3',
      account_id: '3',
      address_id: '3',
      frozen: true,
      is_cancelled: false,
      created_at: '2025-08-14T13:42:41.213Z',
      updated_at: '2025-08-14T13:42:42.950Z',
      cancelled_at: null,
      bank_debit_card_external_id: 'blue_penguin#3',
      daily_spend_limit: 1000,
      thirty_day_spend_limit: 10000,
      physical_card_activated_timestamp: '2025-09-04T18:12:30.502Z',
      banned_spend_categories: ['Tattoo & Piercing', 'Pawn Shops'],
      truck: {
        truck_id: '3',
        customer_id: 'demo-customer',
        created_at: '2025-08-14T13:34:10.668Z',
        vehicle_id_number: '1EFEFGSGSESE',
        truck_display_name: 'Unit 3',
        truck_make: 'kenworth',
        truck_model: 'w9 series',
        truck_year: 2024,
        ownership_status: 'owned',
        engine_condition: 'unmodified',
        truck_class: 'Heavy Duty',
        truck_class_number: 8,
        starting_mileage: 350,
        current_mileage: 350,
        typical_annual_mileage: 350,
        tire_brand_quality: 'premium',
        truck_payment_amount: '2200.00',
        insurance_payment_amount: '17.00',
        pay_for_repairs: true,
        external_eld_vehicle_id: null,
        eld_id: null,
        eld_type: null,
        eld_vehicle_linked_at: null,
        deleted_at: null,
        is_deleted: false,
        show_asset_image: true,
      },
      address: {
        address_id: '3',
        customer_id: 'demo-customer',
        address_st_number: '123',
        address_st_name: 'Main St',
        address_unit: null,
        address_city: 'Atlanta',
        address_state: 'GA',
        address_zip: '30328',
        is_primary: true,
        is_deleted: false,
        created_at: '2025-09-17T00:49:40.666Z',
        updated_at: '2025-09-17T00:49:40.666Z',
        deleted_at: null,
      },
    },
    {
      debit_card_id: '4',
      last_four_digits: '4499',
      customer_id: 'demo-customer',
      truck_id: '4',
      account_id: '4',
      address_id: '4',
      frozen: false,
      is_cancelled: true,
      created_at: '2025-09-29T18:18:38.318Z',
      updated_at: '2025-09-29T18:18:40.491Z',
      cancelled_at: null,
      bank_debit_card_external_id: 'blue_penguin#4',
      daily_spend_limit: 1000,
      thirty_day_spend_limit: 10000,
      physical_card_activated_timestamp: '2025-10-16T19:04:03.218Z',
      banned_spend_categories: [
        'Gambling',
        'Jewelry & Watches',
        'Amusement Parks',
        'Pawn Shops',
      ],
      truck: {
        truck_id: '4',
        customer_id: 'demo-customer',
        created_at: '2025-08-12T21:30:31.248Z',
        vehicle_id_number: '1FEGSESGEFDE',
        truck_display_name: 'Unit 2',
        truck_make: 'freightliner',
        truck_model: 'm2',
        truck_year: 2020,
        ownership_status: 'owned',
        engine_condition: 'unmodified',
        truck_class: 'Heavy Duty',
        truck_class_number: 7,
        starting_mileage: 300,
        current_mileage: 300,
        typical_annual_mileage: 300,
        tire_brand_quality: 'budget',
        truck_payment_amount: '12.00',
        insurance_payment_amount: '800.00',
        pay_for_repairs: true,
        external_eld_vehicle_id: null,
        eld_id: null,
        eld_type: null,
        eld_vehicle_linked_at: null,
        deleted_at: null,
        is_deleted: false,
        show_asset_image: false,
      },
      address: {
        address_id: '4',
        customer_id: 'demo-customer',
        address_st_number: '123',
        address_st_name: 'Main St',
        address_unit: null,
        address_city: 'Atlanta',
        address_state: 'GA',
        address_zip: '30328',
        is_primary: false,
        is_deleted: false,
        created_at: '2025-09-29T18:18:23.586Z',
        updated_at: '2025-09-29T18:18:23.586Z',
        deleted_at: null,
      },
    },
  ] as any[];
};

// ============================================================================
// Demo Transactions
// ============================================================================

export const getDemoTransactions = () => {
  const today = new Date();
  const transactions = [];

  // Helper function to generate realistic income deposit with payments
  const generateIncomeDeposit = (
    amount: number,
    daysAgo: number,
    index: number,
    truckId: string
  ) => {
    const transactionDate = new Date(today);
    transactionDate.setDate(transactionDate.getDate() - daysAgo);

    // Calculate payment breakdown:
    // 2% TACH fee, then split remaining between budgeted funds (~60%) and take-home (~40%)
    const tachFee = Math.round(amount * 0.02 * 100) / 100; // 2% fee
    const afterFee = amount - tachFee;
    const budgetedFunds = Math.round(afterFee * 0.55 * 100) / 100; // 60% to budgets
    const takeHome = Math.round(afterFee * 0.45 * 100) / 100; // 40% to take-home

    return {
      transaction_id: `demo-income-deposit-${index}`,
      customer_id: 'demo-customer',
      account_id: 'demo-account-id',
      debit_card_id: null,
      amount: amount,
      transaction_timestamp: transactionDate.toISOString(),
      merchant_name: null,
      merchant_category_code: null,
      location_raw: '',
      location_st_number: null,
      location_st_name: null,
      location_unit: null,
      location_city: null,
      location_state: null,
      location_zip: null,
      location_country: null,
      description: 'Income Deposit',
      raw_type: 'DEPOSIT',
      created_at: transactionDate.toISOString(),
      tach_initiated: false,
      type: 'deposit',
      category: 'income',
      processing_completed_timestamp: transactionDate.toISOString(),
      processed: true,
      truck_id: truckId,
      load_status: 'loaded',
      truck: null,
      debit_card: null,
      payments: [
        {
          amount: -budgetedFunds * 100, // Convert to cents and negative (allocated away)
          payment_context: 'TACH Budgeted Funds',
          payment_context_display_name: 'TACH Budgeted Funds',
          status_display_name: 'Processed',
          created_at: transactionDate.toISOString(),
        },
        {
          payment_id: `demo-payment-grossprofittransfer-${index}`,
          customer_id: 'demo-customer',
          transaction_id: `demo-income-deposit-${index}`,
          linked_transaction_id: `demo-linked-txn-${index}-1`,
          account_id: 'demo-external-account',
          internal_account_id: 'demo-account-id',
          loan_id: null,
          payment_context: 'grossProfitTransfer',
          amount: takeHome * 100, // Convert to cents (positive, goes to take-home)
          created_at: transactionDate.toISOString(),
          payment_status: 'settled#swept',
          currency: 'usd',
          refund_transaction_id: null,
          status_display_name: 'Processed',
          payment_context_display_name: 'Net Take Home Transfer',
        },
        {
          payment_id: `demo-payment-tachfee-${index}`,
          customer_id: 'demo-customer',
          transaction_id: `demo-income-deposit-${index}`,
          linked_transaction_id: `demo-linked-txn-${index}-2`,
          account_id: null,
          internal_account_id: 'demo-account-id',
          loan_id: null,
          payment_context: 'tachFee',
          amount: tachFee * 100, // Convert to cents
          created_at: transactionDate.toISOString(),
          payment_status: 'settled#unswept',
          currency: 'usd',
          refund_transaction_id: null,
          status_display_name: 'Processed',
          payment_context_display_name: 'TACH Fee',
        },
      ],
    };
  };

  // Generate 6 income deposit transactions with varying amounts ($2000-$5000)
  const incomeDeposits = [
    { amount: 3250, daysAgo: 2, truckId: 'demo-truck-1' },
    { amount: 4850, daysAgo: 5, truckId: 'demo-truck-2' },
    { amount: 2750, daysAgo: 8, truckId: 'demo-truck-1' },
    { amount: 4200, daysAgo: 12, truckId: 'demo-truck-2' },
    { amount: 3900, daysAgo: 16, truckId: 'demo-truck-1' },
    { amount: 2450, daysAgo: 20, truckId: 'demo-truck-2' },
  ];

  incomeDeposits.forEach((deposit, index) => {
    transactions.push(
      generateIncomeDeposit(
        deposit.amount,
        deposit.daysAgo,
        index,
        deposit.truckId
      )
    );
  });

  // Generate 20 realistic transactions over the past 30 days
  const transactionTypes = [
    {
      category: 'fuel',
      merchant: 'Pilot Flying J',
      amountRange: [350, 450],
      card_last_four_digits: '1891',
    },
    {
      category: 'fuel',
      merchant: "Love's Travel Stops",
      amountRange: [350, 420],
      card_last_four_digits: '4521',
    },
    {
      category: 'fuel',
      merchant: 'TA Petro',
      amountRange: [300, 600],
      card_last_four_digits: '4521',
    },
    {
      category: 'preventative_maintenance',
      merchant: 'Truck Service Center',
      amountRange: [1000, 1800],
      card_last_four_digits: '4521',
    },
    {
      category: 'tires',
      merchant: 'Atlanta Commercial Tire',
      amountRange: [2400, 7000],
      card_last_four_digits: '4521',
    },
    {
      category: 'insurance',
      merchant: 'Truck Insurance',
      amountRange: [700, 1500],
      card_last_four_digits: '1891',
    },
    {
      category: 'repairs',
      merchant: 'Truck Service Depot',
      amountRange: [500, 1000],
      card_last_four_digits: '4521',
    },
    {
      category: 'tolls',
      merchant: 'GA Highway',
      amountRange: [35, 50],
      card_last_four_digits: '1891',
    },
  ];

  for (let i = 0; i < 20; i++) {
    const daysAgo = Math.floor(Math.random() * 30);
    const transactionDate = new Date(today);
    transactionDate.setDate(transactionDate.getDate() - daysAgo);

    const type =
      transactionTypes[Math.floor(Math.random() * transactionTypes.length)];
    const amount =
      Math.random() * (type.amountRange[1] - type.amountRange[0]) +
      type.amountRange[0];
    const truckIndex = Math.random() > 0.5 ? 0 : 1;

    transactions.push({
      transaction_id: `demo-txn-${i}`,
      customer_id: 'demo-customer',
      truck_id: `demo-truck-${truckIndex + 1}`,
      amount: -Math.round(amount * 100) / 100, // Negative for expenses
      category: type.category,
      merchant_name: type.merchant,
      merchant_city: truckIndex === 0 ? 'Dallas' : 'Houston',
      merchant_state: 'TX',
      transaction_timestamp: transactionDate.toISOString(),
      description: `${type.merchant} - ${type.category}`,
      status: 'completed',
      debit_card: {
        last_four_digits: type.card_last_four_digits,
      },
      processed: true,
      created_at: transactionDate.toISOString(),
      updated_at: transactionDate.toISOString(),
      truck: {
        truck_id: `demo-truck-${truckIndex + 1}`,
        truck_display_name: `Unit ${truckIndex + 1}`,
        make: truckIndex === 0 ? 'Freightliner' : 'Peterbilt',
        model: truckIndex === 0 ? 'Cascadia' : '579',
      },
      // Mark a few transactions as reversed for testing
      reversal_status: i === 3 || i === 7 || i === 15 ? 'reversed' : null,
    });
  }

  // Sort by date descending (newest first)
  return transactions.sort(
    (a, b) =>
      new Date(b.transaction_timestamp).getTime() -
      new Date(a.transaction_timestamp).getTime()
  ) as any[];
};

// ============================================================================
// Demo Financial Overview Data
// ============================================================================

export const getDemoEarliestDate = () => {
  // Return date from 6 months ago
  const date = new Date();
  date.setMonth(date.getMonth() - 6);
  return date.toISOString().split('T')[0];
};

export const getDemoCpmRpmData = () => {
  // Generate 6 months of CPM/RPM data
  const data = [];
  const today = new Date();

  for (let i = 5; i >= 0; i--) {
    const date = new Date(today);
    date.setMonth(date.getMonth() - i);

    // Realistic CPM ranges from $1.50 to $2.00
    const cpm = 1.5 + Math.random() * 0.5;
    // Realistic RPM ranges from $2.50 to $3.50
    const rpm = 2.5 + Math.random() * 1.0;

    data.push({
      month: date.getMonth().toString(),
      year: date.getFullYear().toString(),
      cpm: parseFloat(cpm.toFixed(2)),
      rpm: parseFloat(rpm.toFixed(2)),
    });
  }

  return data;
};

export const getDemoTotalMilesDriven = () => {
  // Return total miles for demo period (e.g., 45,000 miles over 6 months)
  return 45230;
};

export const getDemoTrucksRevenueExpenseData = () => {
  const trucks = getDemoTrucks();

  return trucks.map((truck, index) => {
    // Generate realistic revenue and expense data
    const revenue = 115000 + Math.random() * 30000; // $85k-$115k

    // Create realistic expense breakdown by category
    const fuelExpense = 25000 + Math.random() * 8000; // $25k-$33k
    const maintenanceExpense = 9000 + Math.random() * 2000; // $8k-$12k
    const insuranceExpense = 6000 + Math.random() * 3000; // $6k-$9k
    const tiresExpense = 3000 + Math.random() * 200; // $2k-$3.5k
    const otherExpense = 3000 + Math.random() * 2000; // $3k-$5k
    const truckPaymentExpense = 8000 + Math.random() * 500; // $10k-$13k
    const expenses = [
      {
        category: 'Fuel',
        amount: parseFloat(fuelExpense.toFixed(2)),
        color: '#EF4444', // Red
      },
      {
        category: 'Maintenance',
        amount: parseFloat(maintenanceExpense.toFixed(2)),
        color: '#F59E0B', // Orange
      },
      {
        category: 'Truck Payment',
        amount: parseFloat(truckPaymentExpense.toFixed(2)),
        color: '#F38182', // Reddish Orange
      },
      {
        category: 'Insurance',
        amount: parseFloat(insuranceExpense.toFixed(2)),
        color: '#8B5CF6', // Purple
      },
      {
        category: 'Preventative Maintenance',
        amount: parseFloat(otherExpense.toFixed(2)),
        color: '#6B7280', // Gray
      },
      {
        category: 'Tires',
        amount: parseFloat(tiresExpense.toFixed(2)),
        color: '#3B82F6', // Blue
      },
    ];

    const totalExpenses = expenses.reduce((sum, exp) => sum + exp.amount, 0);
    const netTakeHome = revenue - totalExpenses;
    const remainingRevenue = revenue - netTakeHome;
    return {
      truckId: truck.truck_id,
      truckName: truck.truck_display_name,
      truckMake: truck.make,
      truckModel: truck.model,
      truckYear: truck.year,
      revenue: parseFloat(remainingRevenue.toFixed(2)),
      netTakeHome: parseFloat(netTakeHome.toFixed(2)),
      expenses: expenses,
    };
  });
};

export const getDemoBudgetedVsTargetData = () => {
  // Generate 6 months of budgeted vs actual data
  const data = [];
  const today = new Date();

  for (let i = 5; i >= 0; i--) {
    const date = new Date(today);
    date.setMonth(date.getMonth() - i);

    // Realistic target amounts
    const target = 12000 + Math.random() * 2000; // $12k-$14k
    // Actual spending slightly varies
    const actual = target * (0.85 + Math.random() * 0.25); // 85-110% of target

    data.push({
      month: date.getMonth().toString(), // Numeric month (0-11) as string
      target: parseFloat(target.toFixed(2)),
      actual: parseFloat(actual.toFixed(2)),
    });
  }

  return data;
};

// ============================================================================
// Demo Monthly Budget Data
// ============================================================================

export const getDemoMonthlyBudgetMetrics = () => {
  // For a carrier making $25k/month with $13,750 budgeted for expenses (55%)
  // All amounts are in actual dollars
  return [
    {
      category: 'fuel',
      category_display_name: 'Fuel',
      amount: -1500.0, // NEGATIVE: -$1,500 (overspent this month due to fuel price spike)
      time_window_in_months: 1,
      emoji_unicode: 'U+26FD',
      budget_bucket_limit: 9000.0, // $9,000/month budget (36% of revenue)
      rollover: -1500.0, // Negative balance carries over
      spent: 10500.0, // Spent $10,500 when budget was only $9,000
      saved: 7000,
      isCustom: false,
      monthly_goal: 9000.0, // Monthly budget
      recent_transactions: [],
    },
    {
      category: 'repairs',
      category_display_name: 'Repairs & Parts',
      amount: 22000.0, // OVERFILLED: $22,000 (saved $4,000 more than annual goal)
      time_window_in_months: 12,
      emoji_unicode: 'U+1F6E0',
      budget_bucket_limit: 18000.0, // $18,000/year budget ($1,500/month)
      rollover: 22000.0, // Extra savings roll over
      spent: 1250, // Haven't needed major repairs yet this year
      saved: 4000.0, // Saved $4,000 beyond the goal
      isCustom: false,
      monthly_goal: 1500.0, // $1,500/month (18000/12)
      recent_transactions: [],
    },
    {
      category: 'tires',
      category_display_name: 'Tires',
      amount: 3000.0, // $3,000 current balance (50% funded)
      time_window_in_months: 12,
      emoji_unicode: 'U+1F6DE',
      budget_bucket_limit: 6000.0, // $6,000/year budget ($500/month)
      rollover: 3000.0,
      spent: 0,
      saved: 0,
      isCustom: false,
      monthly_goal: 500.0, // $500/month (6000/12)
      recent_transactions: [],
    },
    {
      category: 'preventative_maintenance',
      category_display_name: 'PMs',
      amount: 825.0, // $825 current balance (55% funded)
      time_window_in_months: 3,
      emoji_unicode: 'U+1F527',
      budget_bucket_limit: 1500.0, // $1,500/3 months budget ($500/month)
      rollover: 825.0,
      spent: 1107.43,
      saved: 692.57,
      isCustom: false,
      monthly_goal: 500.0, // $500/month (1500/3)
      recent_transactions: [],
    },
    {
      category: 'licensing',
      category_display_name: 'Licensing & Fees',
      amount: 1800.0, // $1,800 current balance (75% funded)
      time_window_in_months: 12,
      emoji_unicode: 'U+1F4C4',
      budget_bucket_limit: 2400.0, // $2,400/year budget ($200/month)
      rollover: 1800.0,
      spent: 250,
      saved: 0,
      isCustom: false,
      monthly_goal: 200.0, // $200/month (2400/12)
      recent_transactions: [],
    },
    {
      category: 'tolls',
      category_display_name: 'Tolls',
      amount: 2940.0, // $2,940 current balance (70% funded)
      time_window_in_months: 12,
      emoji_unicode: 'U+1F6E3',
      budget_bucket_limit: 4200.0, // $4,200/year budget ($350/month)
      rollover: 2940.0,
      spent: 0,
      saved: 0,
      isCustom: false,
      monthly_goal: 350.0, // $350/month (4200/12)
      recent_transactions: [],
    },
    {
      category: 'insurance',
      category_display_name: 'Insurance',
      amount: 1080.0, // $1,080 current balance (90% funded)
      time_window_in_months: 1,
      emoji_unicode: 'U+1F6E1',
      budget_bucket_limit: 1200.0, // $1,200/month budget (4.8% of revenue)
      rollover: 1080.0,
      spent: 0,
      saved: 0,
      isCustom: false,
      monthly_goal: 1200.0, // Monthly budget
      recent_transactions: [],
    },
    {
      category: 'truck_payments',
      category_display_name: 'Truck Payments',
      amount: 0,
      time_window_in_months: 1,
      emoji_unicode: 'U+1F69A',
      budget_bucket_limit: 0, // Paid separately outside TACH
      rollover: 0,
      spent: 0,
      saved: 0,
      isCustom: false,
      monthly_goal: 0,
      recent_transactions: [],
    },
    {
      category: 'custom_new_truck_fund',
      category_display_name: 'New Truck Fund',
      amount: 490.0, // $490 current balance (98% funded)
      time_window_in_months: 1,
      emoji_unicode: 'U+1F6FB',
      budget_bucket_limit: 500.0, // $500/month budget
      rollover: 490.0,
      spent: 0,
      saved: 0,
      isCustom: true, // This is a custom category
      customer_selected_amount: 500.0,
      monthly_goal: 500.0, // Monthly budget
      recent_transactions: [],
    },
  ];
};

export const getDemoDollarBreakdownData = () => {
  // For a carrier making ~$25,000/month with ~55% going to business expenses
  return [
    {
      name: 'Budgeting',
      category: 'budgeting',
      centsPerDollar: 57.88,
      type: 'budgeting',
      emoji_unicode: 'U+1F5C2',
      subCategories: [
        {
          name: 'Fuel',
          category: 'fuel',
          centsPerDollar: 36.59,
          type: 'budgeting',
          emoji_unicode: 'U+26FD',
        },
        {
          name: 'Preventative Maintenance',
          category: 'fuel',
          centsPerDollar: 2.54,
          type: 'budgeting',
          emoji_unicode: 'U+1F6E0',
        },
        {
          name: 'Insurance',
          category: 'insurance',
          centsPerDollar: 5.94,
          type: 'budgeting',
          emoji_unicode: 'U+1F9FE',
        },
        {
          name: 'Repairs & Parts',
          category: 'repairs',
          centsPerDollar: 7.35,
          type: 'budgeting',
          emoji_unicode: 'U+2699',
        },
      ],
    },
    {
      name: 'Net Take Home',
      category: 'gross_profit',
      centsPerDollar: 40.12,
      type: 'gross_profit',
      emoji_unicode: 'U+1F4B0',
      subCategories: [],
    },
    {
      name: 'TACH Fee',
      category: 'tach_fee',
      centsPerDollar: 1.49,
      type: 'tach_fee',
      emoji_unicode: 'U+1F9FE',
      subCategories: [],
    },
    {
      name: 'Cash Advances',
      category: 'cash_advance_repayment',
      centsPerDollar: null,
      type: 'cash_advance_repayment',
      emoji_unicode: 'U+1F4B8',
      subCategories: [],
    },
  ];
};

export const getDemoTrucksBudgetedSpentData = () => {
  const trucks = getDemoTrucks();

  return trucks.map((truck) => {
    // Generate realistic budgeted and spent amounts
    const fuelBudgeted = 8000;
    const fuelSpent = 6000 + Math.random() * 3000; // $6k-$9k

    const maintenanceBudgeted = 3000;
    const maintenanceSpent = 2000 + Math.random() * 1500; // $2k-$3.5k

    const insuranceBudgeted = 2000;
    const insuranceSpent = 1800 + Math.random() * 400; // $1.8k-$2.2k

    const tollsBudgeted = 1000;
    const tollsSpent = 800 + Math.random() * 400; // $800-$1200

    const budgetedCategories = [
      {
        category: 'Fuel',
        amount: fuelBudgeted,
        color: '#EF4444', // Red
      },
      {
        category: 'Maintenance',
        amount: maintenanceBudgeted,
        color: '#F59E0B', // Orange
      },
      {
        category: 'Insurance',
        amount: insuranceBudgeted,
        color: '#8B5CF6', // Purple
      },
      {
        category: 'Tolls',
        amount: tollsBudgeted,
        color: '#3B82F6', // Blue
      },
    ];

    const spentCategories = [
      {
        category: 'Fuel',
        amount: parseFloat(fuelSpent.toFixed(2)),
        color: '#EF4444',
      },
      {
        category: 'Maintenance',
        amount: parseFloat(maintenanceSpent.toFixed(2)),
        color: '#F59E0B',
      },
      {
        category: 'Insurance',
        amount: parseFloat(insuranceSpent.toFixed(2)),
        color: '#8B5CF6',
      },
      {
        category: 'Tolls',
        amount: parseFloat(tollsSpent.toFixed(2)),
        color: '#3B82F6',
      },
    ];

    const totalBudgeted = budgetedCategories.reduce(
      (sum, cat) => sum + cat.amount,
      0
    );
    const totalSpent = spentCategories.reduce(
      (sum, cat) => sum + cat.amount,
      0
    );

    return {
      truckId: truck.truck_id,
      truckName: truck.truck_display_name,
      truckMake: truck.make,
      truckModel: truck.model,
      truckYear: truck.year,
      totalBudgeted,
      totalSpent: parseFloat(totalSpent.toFixed(2)),
      budgetedCategories,
      spentCategories,
    };
  });
};

// ============================================================================
// Demo Drivers and Invites (Non-Owner Only)
// ============================================================================

/**
 * Returns demo drivers and invites for pre-active customers.
 * NOTE: This does NOT include the owner - the owner should be constructed
 * from real userRecord.driver data in the component.
 */
export const getDemoDriversAndInvites = () => {
  const today = new Date();

  return [
    // Active driver
    {
      object_id: 'demo-driver-1',
      customer_id: 'demo-customer',
      first_name: 'Mike',
      last_name: 'Johnson',
      email: 'mike.johnson@example.com',
      phone_number: '+15125550142',
      truck_id: 'demo-truck-2',
      status: 'active',
      is_driver: true,
      is_primary_owner: false,
      created_at: new Date('2024-02-15'),
      updated_at: today,
    },
    // Pending invite
    {
      object_id: 'demo-invite-1',
      customer_id: 'demo-customer',
      first_name: 'Sarah',
      last_name: 'Williams',
      email: 'sarah.williams@example.com',
      phone_number: null,
      truck_id: 'demo-truck-1',
      status: 'pending',
      is_driver: false,
      is_primary_owner: false,
      created_at: new Date('2024-03-01'),
      updated_at: new Date('2024-03-01'),
    },
  ];
};

// ============================================================================
// Helper: Check if customer is pre-active
// ============================================================================

export const isPreActiveCustomer = (status?: string): boolean => {
  return status === 'pre_active';
};

// ============================================================================
// Demo Data Message
// ============================================================================

export const DEMO_DATA_MESSAGE =
  'This is preview data to help you explore TACH features. Real data will be available once you complete onboarding and your account is approved.';

export const DEMO_DATA_SHORT_MESSAGE =
  'Preview data - complete onboarding to unlock real data';
